import type { BootError, BootResult, ErrorSource } from 'owa-shared-start-types';
import { isExplicitLogonSession } from 'owa-config/lib/isExplicitLogonSession';
import {
    isMemoryError,
    isNonOwaError,
    isOneOf,
    isConfigurationError,
    isNetworkError,
    isThrottlingError,
    isTransientError,
    isMsalNeedNlpRedirectError,
    isMsalAadOrMsaRedirectError,
    isNoWebViewError,
} from 'owa-errors';
import type ErrorDiagnostics from './interfaces/ErrorDiagnostics';
import isActivityTimeoutAuthError from 'owa-service/lib/isActivityTimeoutAuthError';
import { isMsalFlowEnabled } from 'owa-msaljs/lib/isMsalFlowEnabled';
import { getQueryStringParameters } from 'owa-querystring';
import { MSAL_AUTH_REDIRECT_KEY } from './redirect';

const statusToBootResultMapping: {
    [status: number]: BootResult;
} = {
    0: 'network',
    401: 'auth',
    411: 'external',
    440: 'auth',
};

const whiteListedErrorSources: ErrorSource[] = ['ExpiredBuild', 'PoisonedBuild', 'UpdateFailed'];
const invalidLanguageExceptionList = [
    'OwaInvalidTimezoneException',
    'OwaInvalidOperationException',
    'OwaInvalidUserLanguageException',
];

// all of these exceptions happen when you try to open another mailbox that you don't have access to
const explicitLogonAccessDeniedExceptions: string[] = [
    'AccessDeniedException',
    'MailboxUnavailableException',
    'OwaExplicitLogonException',
    'OwaADObjectNotFoundException',
];

const otherEnvironmentUrlException = 'OwaOtherEnvironmentUrlException';

export default function calculateBootResult(
    error: BootError | undefined,
    errorDiagnostics: ErrorDiagnostics
): BootResult {
    if (error?.result) {
        return error.result;
    }

    const errorMessage = errorDiagnostics.err;
    if (isMsalNeedNlpRedirectError(errorMessage)) {
        return 'nlp';
    }
    if (isOneOf(invalidLanguageExceptionList, errorMessage)) {
        return 'langtz';
    }
    if (isOneOf([otherEnvironmentUrlException], errorMessage)) {
        return 'otherEnvUrl';
    }
    if (error?.abortError) {
        return 'abort';
    }
    if (isThrottlingError(errorMessage)) {
        return 'throttle';
    }
    if (isConfigurationError(errorMessage)) {
        return 'configuration';
    }

    // check if the current error message or any previous errors messages are a memory error
    if (
        isMemoryError(errorMessage) ||
        (error && isNonOwaError(error.stack)) ||
        (window.owaBackfilledErrors &&
            window.owaBackfilledErrors.filter(args => isMemoryError(args[0])).length > 0)
    ) {
        return 'memory';
    }
    if (error) {
        const response = error.response;
        if (response?.url && response.url.toLowerCase().indexOf('/auth/errorfe.aspx') > -1) {
            return 'errorfe';
        }
        // if the error has a request object, then webpack or fetchLocStrings
        // tried to download a resource from the CDN but couldn't. In these cases
        // we have failed because there is a problem with the CDN or a user has a proxy
        // or firewall setup that messes with the request. We will track these separately
        // than startup errors
        if (error.request) {
            return 'cdnError';
        }
    }
    const source = errorDiagnostics.esrc;
    if (source && whiteListedErrorSources.indexOf(source) > -1) {
        return <BootResult>source;
    }
    const status = errorDiagnostics.st;
    if (source == 'InitLoc' && status != 0) {
        // Loc files are loaded from the CDN, which we expect should always succeed. Any failure
        // (other than 'network') should be treated as 'fail'.
        return 'fail';
    }
    if (
        isExplicitLogonSession(window) &&
        isOneOf(explicitLogonAccessDeniedExceptions, errorMessage)
    ) {
        return 'accessDenied';
    }
    if (isTransientError(errorMessage) || status == 503) {
        return 'transient';
    }
    if (isNetworkError(errorMessage, error)) {
        return 'network';
    }
    if (isActivityTimeoutAuthError(<number>status, error?.response?.headers)) {
        return 'abt';
    }

    if (isMsalFlowEnabled()) {
        const params = getQueryStringParameters(location);

        // fail boot upon second consecutive MSAL auth error
        if (params[MSAL_AUTH_REDIRECT_KEY] === 'true') {
            return 'fail';
        }
    }

    if (isMsalAadOrMsaRedirectError(errorMessage)) {
        return 'aadmsaredir';
    }

    if (isNoWebViewError(errorMessage)) {
        return 'nowebview';
    }

    if (source === 'OutdatedPIE') {
        return 'OutdatedPIE';
    }

    if (source === 'MissingInteropAPI') {
        return 'MissingInteropAPI';
    }

    return statusToBootResultMapping[<number>status] || 'fail';
}
